import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Sparkles, CheckCircle2, ArrowRight, FolderTree, SkipForward, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../fetchInterceptor';
import InfoTooltip from '../components/InfoTooltip';
import { clearSetupGate } from '../lib/setupGate';
import { trackFunnel } from '../lib/funnelTracker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INDUSTRIES = [
  { value: 'general', label: 'General Business' },
  { value: 'accounting', label: 'Accounting / CPA Firm' },
  { value: 'tax', label: 'Tax Preparation' },
  { value: 'law', label: 'Law Firm' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'construction', label: 'Construction' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'restaurant', label: 'Restaurant / Hospitality' },
  { value: 'nonprofit', label: 'Nonprofit' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'marketing', label: 'Marketing Agency' },
  { value: 'education', label: 'Education' },
  { value: 'creative', label: 'Creative / Design' },
  { value: 'fitness', label: 'Fitness / Wellness' },
];

const SPACE_LABEL = {
  firm: 'Organization', personal: 'My Files', client: 'Clients', vendor: 'Vendors', prospect: 'Prospects',
};

export default function WorkspaceSetupWizard() {
  const navigate = useNavigate();
  const { workspaceId } = useParams();

  const [industry, setIndustry] = useState('general');
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [setupCompleted, setSetupCompleted] = useState(false);

  const fetchSuggestions = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const r = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/setup-suggestions?industry=${industry}`);
      if (r.ok) {
        const data = await r.json();
        setSuggestions(data.suggestions || []);
        setSetupCompleted(!!data.setup_completed);
        // Auto-select all by default — single click "Apply"
        setSelected(new Set((data.suggestions || []).map(s => s.template_id)));
      }
    } finally { setLoading(false); }
  }, [workspaceId, industry]);

  useEffect(() => { fetchSuggestions(); }, [fetchSuggestions]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // After the wizard, resume the staged Stripe checkout (if any) — the
  // setup gate deferred it during workspace creation.
  const postWizardDest = (fallback, { skipped = false } = {}) => {
    clearSetupGate();
    trackFunnel('wizard.completed', { extra: { skipped } });
    try {
      const raw = window.localStorage.getItem('plan_intent');
      if (raw) {
        const i = JSON.parse(raw);
        if (i && i.plan_id) {
          // Refresh the TTL — time spent in the wizard shouldn't expire it.
          window.localStorage.setItem('plan_intent', JSON.stringify({ ...i, ts: Date.now() }));
          return '/settings?tab=billing&auto=1';
        }
      }
    } catch { /* ignore */ }
    return fallback;
  };

  const applySelected = async () => {
    if (selected.size === 0) {
      toast.info('Pick at least one template, or click Skip to finish without applying.');
      return;
    }
    setApplying(true);
    try {
      const r = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/setup-apply`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ template_ids: Array.from(selected), industry }),
      });
      if (r.ok) {
        const data = await r.json();
        const folderCount = (data.applied || []).reduce((acc, a) => acc + (a.folders_created || 0), 0);
        toast.success(`Created ${folderCount} folder${folderCount === 1 ? '' : 's'} from ${data.applied.length} template${data.applied.length === 1 ? '' : 's'}`);
        navigate(postWizardDest('/documents'));
      } else { toast.error('Failed to apply templates'); }
    } finally { setApplying(false); }
  };

  const skip = async () => {
    setApplying(true);
    try {
      const r = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/setup-skip`, { method: 'POST' });
      if (r.ok) navigate(postWizardDest('/dashboard', { skipped: true }));
    } finally { setApplying(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-background to-blue-50 dark:from-emerald-950/20 dark:via-background dark:to-blue-950/20 py-12 px-4">
      <div className="max-w-3xl mx-auto space-y-6" data-testid="setup-wizard-page">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-blue-500 mb-3">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Welcome — let's get you set up</h1>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto">
            Pick your industry and we'll instantiate the most relevant folder templates so you don't start from a blank canvas.
            You can fully customize everything later.
          </p>
        </div>

        {/* Industry picker */}
        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Industry</label>
              <InfoTooltip
                content={
                  <p className="text-xs">Picking an industry filters the recommended templates to the ones our customers in that vertical use most.</p>
                }
              />
            </div>
            <Select value={industry} onValueChange={setIndustry}>
              <SelectTrigger data-testid="wizard-industry-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {INDUSTRIES.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Suggestions */}
        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h2 className="font-semibold text-base flex items-center gap-2">
                  <FolderTree className="w-4 h-4 text-emerald-500" />
                  Recommended templates
                </h2>
                <p className="text-xs text-muted-foreground">All selected by default — uncheck anything you don't want.</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setSelected(new Set(suggestions.map(s => s.template_id)))} data-testid="wizard-select-all">Select all</Button>
                <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} data-testid="wizard-clear">Clear</Button>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading suggestions…
              </div>
            ) : suggestions.length === 0 ? (
              <p className="text-sm text-muted-foreground italic py-6 text-center">No suggestions for this industry yet — pick another or skip.</p>
            ) : (
              <div className="grid gap-2" data-testid="wizard-suggestions">
                {suggestions.map(s => {
                  const checked = selected.has(s.template_id);
                  return (
                    <button
                      key={s.template_id}
                      onClick={() => toggle(s.template_id)}
                      className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-all ${
                        checked ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/20' : 'border-border bg-card hover:bg-muted/30'
                      }`}
                      data-testid={`wizard-tpl-${s.template_id}`}
                    >
                      <div className={`mt-0.5 w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${
                        checked ? 'bg-emerald-500 text-white' : 'border border-input'
                      }`}>
                        {checked && <CheckCircle2 className="w-3.5 h-3.5" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-medium text-sm">{s.name}</span>
                          <Badge variant="outline" className="text-[10px]">{SPACE_LABEL[s.space] || s.space}</Badge>
                          {s.industry && <Badge variant="secondary" className="text-[10px] capitalize">{s.industry.replace(/_/g, ' ')}</Badge>}
                          <Badge variant="outline" className="text-[10px]">{s.node_count} folder{s.node_count === 1 ? '' : 's'}</Badge>
                        </div>
                        {s.description && <p className="text-xs text-muted-foreground mt-0.5">{s.description}</p>}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-between gap-3">
          <Button variant="ghost" onClick={skip} disabled={applying} data-testid="wizard-skip">
            <SkipForward className="w-4 h-4 mr-1.5" />
            Skip for now
          </Button>
          <Button
            onClick={applySelected}
            disabled={applying || loading || suggestions.length === 0}
            className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white"
            data-testid="wizard-apply"
          >
            {applying ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-1.5" />}
            Apply {selected.size} template{selected.size === 1 ? '' : 's'} & continue
          </Button>
        </div>

        {setupCompleted && (
          <p className="text-center text-xs text-muted-foreground italic">
            You've already completed setup for this workspace. Applying again will add additional folders.
          </p>
        )}
      </div>
    </div>
  );
}
